import * as styles from "./SectionBreedSearch.module.scss";

import qs from "qs";
import React, { useEffect, useState } from "react";
import { Button, Form } from "../../../components/common";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import { useStaticData } from "../../../hooks/useStaticData";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";

export interface SectionBreedSearchProps {
	heading: string;
	iconSrc?: string;
	iconAlt?: string;
	jumpLinksTitle?: string;
}

export const getSectionBreedSearchProps = ({
	field_front_end_title,
	field_jump_links_title,
	relationships: { field_icon },
}: Queries.SectionDataFragment) => ({
	heading: field_front_end_title,
	jumpLinksTitle: field_jump_links_title,
	iconSrc: field_icon?.field_media_image?.uri,
	iconAlt: field_icon?.field_media_image?.alt,
});
// TODO - TEST STATIC
export const SectionBreedSearch: React.FC<SectionBreedSearchProps> = ({
	heading,
	jumpLinksTitle,
	iconAlt,
	iconSrc,
}) => {
	const { dogBreeds, dogSheddingNames, dogSizeNames, dogActivityLevels } =
		useStaticData([
			"dogBreeds",
			"dogSheddingNames",
			"dogSizeNames",
			"dogActivityLevels",
		]);

	const [selectedBreed, _setSelectedBreed] = useState("Any");
	const [selectedSize, setSelectedSize] = useState("Any");
	const [selectedShedding, setSelectedShedding] = useState("Any");
	const [selectedActivityLevel, setSelectedActivityLevel] = useState("Any");

	const [_availableBreeds, setAvailableBreeds] = useState<string[]>([]);
	const [availableSizes, setAvailableSizes] = useState<string[]>([]);
	const [availableSheddings, setAvailableSheddings] = useState<string[]>([]);
	const [availableActivityLevels, setAvailableActivityLevels] = useState<
		string[]
	>([]);
	const [resultCount, setResultCount] = useState<number>(0);

	// Update available breeds, sizes, and sheddings when any of the filters change
	useEffect(() => {
		// Filter breeds based on selected Dog size and Shedding
		const filteredBreeds = dogBreeds
			?.filter(
				(option) =>
					(selectedSize === "Any" ||
						option.relationships.field_size.name === selectedSize) &&
					(selectedShedding === "Any" ||
						option.relationships.field_shedding.name === selectedShedding) &&
					(selectedActivityLevel === "Any" ||
						option.relationships.field_activity_level?.name ===
							selectedActivityLevel),
			)
			.sort((a, b) => a.title.localeCompare(b.title))
			.map((option) => option.title);

		setAvailableBreeds(filteredBreeds);

		// Set resultCount to 1 if a specific breed is selected
		setResultCount(selectedBreed !== "Any" ? 1 : filteredBreeds.length);

		// Update available sizes based on the selected breeds
		const filteredSizes = dogSizeNames.filter((breedName) => {
			return (
				selectedBreed === "Any" || // Show all sizes if no breed selected
				dogBreeds.some(
					(breed) =>
						breed.title === selectedBreed &&
						breed.relationships.field_size.name === breedName,
				)
			);
		});

		setAvailableSizes(filteredSizes);

		// Update available sheddings based on the selected breeds
		const filteredSheddings = dogSheddingNames.filter((breedName) => {
			return (
				selectedBreed === "Any" || // Show all sheddings if no breed selected
				dogBreeds.some(
					(breed) =>
						breed.title === selectedBreed &&
						breed.relationships.field_shedding.name === breedName,
				)
			);
		});

		setAvailableSheddings(filteredSheddings);

		// Update available activity levels based on the selected breeds
		const filteredActivityLevels = dogActivityLevels.filter((breedName) => {
			return (
				selectedBreed === "Any" || // Show all activity levels if no breed selected
				dogBreeds.some(
					(breed) =>
						breed.title === selectedBreed &&
						breed.relationships.field_activity_level.name === breedName,
				)
			);
		});

		setAvailableActivityLevels(filteredActivityLevels);
	}, [
		selectedSize,
		selectedShedding,
		selectedBreed,
		selectedActivityLevel,
		dogBreeds,
		dogSizeNames,
		dogSheddingNames,
		dogActivityLevels,
	]);

	const buttonLabel = resultCount === 1 ? "Breed" : "Breeds";

	return (
		<WrappedSection
			sectionName="SectionBreedSearch"
			sectionColour={Colours.SANDY_FUR}
		>
			<div className={styles.breedsearchinner}>
				<div className={styles.imagecontainer}>
					{iconSrc && (
						<ProcessedImage
							className={styles.image}
							src={iconSrc}
							alt={iconAlt}
							dimensions="200x200"
							width={200}
							height={200}
							loading={"lazy"}
						/>
					)}
				</div>
				<div className={styles.formcontainer}>
					{jumpLinksTitle ? (
						<h2 data-jumplinkstitle={jumpLinksTitle} className={styles.heading}>
							{heading}
						</h2>
					) : (
						<h2 className={styles.heading}>{heading}</h2>
					)}
					<Form
						className={styles.breedsearchform}
						onSubmitFn={() => {}}
						validationSchema={{}}
					>
						{/* <div className={styles.filter}>
							<label htmlFor="dog-breeds-input">Breed</label>
							<select
								className={styles.select}
								id="dog-breeds-input"
								name="dog-breeds-input"
								value={selectedBreed}
								onChange={(e) => setSelectedBreed(e.target.value)}
							>
								<option>Any</option>
								{availableBreeds.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div> */}
						<div className={styles.filter}>
							<label htmlFor="dog-size-input">Dog size</label>
							<select
								className={styles.select}
								id="dog-size-input"
								name="dog-size-input"
								value={selectedSize}
								onChange={(e) => setSelectedSize(e.target.value)}
							>
								<option>Any</option>
								{availableSizes.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>
						<div className={styles.filter}>
							<label htmlFor="dog-shedding-input">Shedding</label>
							<select
								className={styles.select}
								id="dog-shedding-input"
								name="dog-shedding-input"
								value={selectedShedding}
								onChange={(e) => setSelectedShedding(e.target.value)}
							>
								<option>Any</option>
								{availableSheddings.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>
						<div className={styles.filter}>
							<label htmlFor="dog-activity-level-input">Activity level</label>
							<select
								className={styles.select}
								id="dog-activity-level-input"
								name="dog-activity-level-input"
								value={selectedActivityLevel}
								onChange={(e) => setSelectedActivityLevel(e.target.value)}
							>
								<option>Any</option>
								{availableActivityLevels.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>
					</Form>
					<div className={styles.btncontainer}>
						<Button
							fullWidth={true}
							url={`/dog-advice/getting-dog/breeds/dogs?${qs.stringify({
								breed: selectedBreed,
							})}&${qs.stringify({
								size: selectedSize,
							})}&${qs.stringify({
								shedding: selectedShedding,
							})}&${qs.stringify({
								activityLevel: selectedActivityLevel,
							})}`}
							variant="primary"
						>
							{`Show (${resultCount}) ${buttonLabel}`}
						</Button>
					</div>
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionBreedSearch;
